import React, { useEffect, useState } from 'react';
import { skillRatings } from '../../api';
import { BRANCHES, findNodeTitlePath } from './data';
import { sumBranchPoints, maxBranchPoints } from './heroClasses';
import { getSkillNodeForTheme } from './themeSkillMap';
import './ThemeSkillChips.css';

function findNodeById(id, nodes = BRANCHES) {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.children) {
      const found = findNodeById(id, node.children);
      if (found) return found;
    }
  }
  return null;
}

function branchLabel(nodeId) {
  const path = findNodeTitlePath(nodeId);
  if (!path) return nodeId;
  return path[path.length - 1];
}

/**
 * Read-only bridge from a Pocket lesson's detected themes to the player's
 * skill tree: shows each theme that maps to a known branch alongside that
 * branch's current progress, and jumps the coach into the Skills tab on
 * click. Deliberately doesn't write ratings itself - bumping a specific
 * leaf from a theme name would be guesswork (one theme can cover a dozen
 * leaves), so this hands the coach off to the real skill tree UI instead.
 */
const ThemeSkillChips = ({ playerId, themes, onNavigateToSkills }) => {
  const [ratings, setRatings] = useState(null);

  const mappable = (themes || [])
    .map(name => ({ name, nodeId: getSkillNodeForTheme(name) }))
    .filter(t => t.nodeId);

  useEffect(() => {
    if (!playerId || mappable.length === 0) return;
    let cancelled = false;
    skillRatings.get(playerId)
      .then(res => {
        if (!cancelled) setRatings(res.data || {});
      })
      .catch(() => {
        if (!cancelled) setRatings({});
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId, (themes || []).join('|')]);

  if (mappable.length === 0) return null;

  return (
    <div className="theme-skill-chips">
      <span className="theme-skill-chips-label">📈 Linked skills:</span>
      {mappable.map(({ name, nodeId }) => {
        const node = findNodeById(nodeId);
        const current = ratings && node ? sumBranchPoints(node, ratings) : null;
        const max = node ? maxBranchPoints(node) : null;

        return (
          <button
            key={nodeId}
            type="button"
            className="theme-skill-chip"
            onClick={() => onNavigateToSkills && onNavigateToSkills(nodeId)}
            title={`Open ${branchLabel(nodeId)} in the skill tree`}
          >
            <span className="theme-skill-chip-name">{branchLabel(nodeId)}</span>
            {current !== null && max !== null && (
              <span className="theme-skill-chip-progress">{current}/{max}</span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default ThemeSkillChips;
